import type { Match } from "navigo";
import { router, setPageContent } from "..";
import { farmcamSimCard } from "../farmcam-sim/fcs_card";
import { fencealarmCard } from "../fencealarm/fa_card";
import { fencealarm2Card } from "../fencealarm2/fa2_card";
import { t } from "../locale";
import {
	addBlurListener,
	addFocusListener,
	MobileFlags,
	onMobile,
	registerNotifications,
	removeBlurListener,
	removeFocusListener,
} from "../mobile";
import { smartplugCard } from "../smartplug/sp_card";
import { smartplug2Card } from "../smartplug2/sp2_card";
import { state } from "../state";
import { binarySearchInsert, binarySearchRemove } from "../utils";
import { Bulma } from "../view/bulma";
import { MaterialDesign } from "../view/material_design";
import type { Device } from "./device";

async function getDeviceColumns<T extends Device>(
	devices: Record<string, T>,
	selectedTags: string[],
	toCard: (device: T) => Promise<HTMLDivElement>,
	registerColumn: (id: string, column: HTMLDivElement) => HTMLDivElement,
): Promise<HTMLDivElement[]> {
	return Promise.all(
		Object.entries(devices)
			.filter(([_id, device]) => {
				if (selectedTags.length < 1) return true;
				if (!device.Metadata.Tags) return false;
				return device.Metadata.Tags.some((tag) =>
					selectedTags.includes(tag),
				);
			})
			.sort(([_id1, device1], [_id2, device2]) =>
				device1.Metadata.Name.localeCompare(device2.Metadata.Name),
			)
			.map(async ([id, device]) => {
				const card = await toCard(device);
				const col = Bulma.column(
					{
						classList: [
							"is-full-mobile",
							"is-narrow-tablet",
							"is-narrow-desktop",
							"is-narrow-widescreen",
							"is-narrow-fullhd",
						],
					},
					card,
				);
				registerColumn(id, col);
				return col;
			}),
	);
}

export async function devicesPage(match: Match | undefined): Promise<void> {
	if (onMobile) {
		MobileFlags.setValue("appFinishedLoading", true);
		if (!MobileFlags.getValue("registeredNotificationsToken")) {
			setTimeout(registerNotifications, 1000);
		}
	}

	const selectedTags: string[] = [];
	const tagControls = state.getDeviceTags().map((tag) => {
		const selectedTag = Bulma.tagWithAddon(
			{
				content: tag,
				color: "is-primary",
			},
			{
				content: MaterialDesign.icon({ id: "remove" }),
				onClick: () => {
					binarySearchRemove(selectedTags, tag);
					refresh();
					selectedTag.replaceWith(deselectedTag);
				},
			},
		);

		const deselectedTag = Bulma.tagWithAddon(
			{
				content: tag,
			},
			{
				content: MaterialDesign.icon({ id: "add" }),
				onClick: () => {
					binarySearchInsert(selectedTags, tag);
					refresh();
					deselectedTag.replaceWith(selectedTag);
				},
			},
		);

		return Bulma.control({}, deselectedTag);
	});

	const deviceColumns: Record<string, HTMLElement> = {};

	const scrollToDevice = (id: string): void =>
		deviceColumns[id].scrollIntoView(false);

	const farmcamSimColumns = Bulma.columns({
		isMobile: true,
		isMultiline: true,
	});
	const fencealarmColumns = Bulma.columns({
		isMobile: true,
		isMultiline: true,
	});
	const fencealarm2Columns = Bulma.columns({
		isMobile: true,
		isMultiline: true,
	});
	const smartplugColumns = Bulma.columns({
		isMobile: true,
		isMultiline: true,
	});
	const smartplug2Columns = Bulma.columns({
		isMobile: true,
		isMultiline: true,
	});

	const refresh = async (): Promise<void> => {
		farmcamSimColumns.replaceChildren(
			...(await getDeviceColumns(
				state.farmcamSims,
				selectedTags,
				farmcamSimCard,
				(id, col) => (deviceColumns[id] = col),
			)),
		);

		fencealarmColumns.replaceChildren(
			...(await getDeviceColumns(
				state.fencealarms,
				selectedTags,
				fencealarmCard,
				(id, col) => (deviceColumns[id] = col),
			)),
		);

		fencealarm2Columns.replaceChildren(
			...(await getDeviceColumns(
				state.fencealarm2s,
				selectedTags,
				fencealarm2Card,
				(id, col) => (deviceColumns[id] = col),
			)),
		);

		smartplugColumns.replaceChildren(
			...(await getDeviceColumns(
				state.smartplugs,
				selectedTags,
				smartplugCard,
				(id, col) => (deviceColumns[id] = col),
			)),
		);

		smartplug2Columns.replaceChildren(
			...(await getDeviceColumns(
				state.smartplug2s,
				selectedTags,
				smartplug2Card,
				(id, col) => (deviceColumns[id] = col),
			)),
		);
	};

	addBlurListener("devices", () => {
		state.stopDevicePolling();
	});

	addFocusListener("devices", () => {
		state.startDevicePolling(refresh);
	});

	await refresh();
	setPageContent(
		Bulma.container(
			Bulma.level(
				Bulma.levelLeft(Bulma.levelItem(Bulma.title(t.devices))),
				Bulma.levelRight(
					Bulma.levelItem(
						MaterialDesign.icon({
							id: "add_circle",
							onClick: () => router.jump("/devices/register"),
							size: 2,
						}),
					),
				),
			),
			Bulma.field(
				{
					styles: ["is-grouped", "is-grouped-multiline"],
				},
				...tagControls,
			),
			farmcamSimColumns,
			fencealarmColumns,
			fencealarm2Columns,
			smartplugColumns,
			smartplug2Columns,
		),
	);

	if (match?.params?.id) {
		scrollToDevice(match.params.id);
	}

	state.startDevicePolling(refresh);
}

export function onLeaveDevicesPage(
	done: Function,
	_match: Match | Match[],
): void {
	state.stopDevicePolling();
	removeBlurListener("devices");
	removeFocusListener("devices");
	done();
}
